import axios from 'axios';
import { getAccessToken, refreshToken, clearAccessToken } from './keycloakService';
import { getEnv, isDevelopment, isDebugMode } from '../envConfig';
import localStorageService from './localStorageService';

const env = getEnv();

const axiosInstance = axios.create({
  baseURL: env.ENDPOINT || '',
  withCredentials: true,
  //timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: attach access token and add request logging
axiosInstance.interceptors.request.use(
  async (config) => {
    const token = getAccessToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;

      // Log token for debugging (only in development)
      if (isDevelopment() && isDebugMode()) {
        console.log('🔑 Token attached to request');
      }
    } else {
      console.warn('⚠️ No access token found');
    }

    // Log request for debugging (only in development and debug mode)
    if (isDevelopment() && isDebugMode()) {
      console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
        headers: {
          ...config.headers,
          Authorization: config.headers.Authorization ? 'Bearer [HIDDEN]' : 'None'
        }
      });
    }

    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor: handle 401, refresh token, and add response logging
axiosInstance.interceptors.response.use(
  (response) => {
    // Log successful response for debugging (only in development and debug mode)
    if (isDevelopment() && isDebugMode()) {
      console.log(`✅ API Response: ${response.status} ${response.config.url}`, {
        data: response.data,
        headers: response.headers
      });
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Log error for debugging (always log errors)
    console.error('❌ API Error:', {
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method,
      message: error.message,
      data: error.response?.data
    });
    // Handle 401 Unauthorized or HTML response (Keycloak login page) - try to refresh token
    const contentType = error.response?.headers?.['content-type'] || '';
    const isHtmlResponse = contentType.toLowerCase().includes('text/html');
    const is401 = error.response?.status === 401;
    const is403 = error.response?.status === 403;
    
    if (((is401 || is403 || isHtmlResponse) && !originalRequest._retry) || (error.message === 'Network Error' && !originalRequest._retry)) {
      originalRequest._retry = true;
      try {
        console.log('🔄 Session expired, attempting to refresh token...');
        const newAccessToken = await refreshToken();
        if (newAccessToken) {
          console.log('✅ Token refreshed successfully');
          // Update the failed request with new token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          // Retry the original request
          return axiosInstance(originalRequest);
        } else {
          throw new Error('No new token received from refresh');
        }
      } catch (refreshError) {
        console.error('❌ Token refresh failed, redirecting to login:', refreshError);
        clearAccessToken();
        localStorageService.removeItem('user');
        localStorageService.removeItem('all_user_roles');
        localStorageService.removeItem('selected_user_role');
        localStorageService.removeItem('refresh_token');
        // Redirect to login
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle other common HTTP errors
    if (error.response) {
      switch (error.response.status) {
        case 403:
          console.error('🚫 Access forbidden - insufficient permissions');
          break;
        case 404:
          console.error('🔍 Resource not found');
          break;
        case 422:
          console.error('⚠️ Validation error:', error.response.data);
          break;
        case 500:
          console.error('💥 Server error - please try again later');
          break;
        default:
          console.error('❓ Unexpected error occurred');
      }
    } else if (error.request) {
      console.error('🌐 Network error - please check your connection',error);
    } else {
      console.error('⚙️ Request configuration error:', error.message);
    }

    return Promise.reject(error);
  }
);

export default axiosInstance; 